import { useCallback, useMemo, useState } from 'react';

import { AnimatedAnswerContainer } from './components/AnimatedAnswerContainer';
import { NavigationFooter } from './components/NavigationFooter';
import { ProgressBar } from './components/ProgressBar';
import { QuestionContent } from './components/QuestionContent';
import { SurveyWindow, SurveyWindowContent, SurveyWindowSection } from './SurveyWindow';

import type { ConfidentialityNoticeProps } from './components/ConfidentialityNotice';
import type { FeedbackPrivacyValue } from '@/constants/review/FeedbackPrivacy';
import type { RespondentStanding } from '@/pages/SurveyAnswer/FeedbackPrivacy/anonymousRespondentCopy';
import type { User } from '@/types/directory/User';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { ReactNode } from 'react';

import { QuestionType } from '@/constants/QuestionType';
import {
    ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES,
    FEEDBACK_PRIVACY_ICONS,
    FeedbackPrivacy,
    isAnonymousFeedback,
    revieweeLaunchedCopy,
} from '@/constants/review/FeedbackPrivacy';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { AnonymousPrivacyDetails } from '@/pages/SurveyAnswer/FeedbackPrivacy/AnonymousPrivacyDetails';

export type SurveyAnswerContainerProps = {
    question: ReviewQuestion;
    renderAnswer: ReactNode | (() => ReactNode);
    isFirst: boolean;
    isLast: boolean;
    onNext: () => void;
    onPrevious?: () => void;
    onSkip?: () => void;
    onSubmit?: () => void;
    isPreview?: boolean;
    backgroundImageUrl?: string;
    showComment?: boolean;
    commentValue?: string;
    onCommentChange?: (value: string) => void;
    commentLabel?: string;
    enableTransitions?: boolean;
    transitionDurationMs?: number;
    transitionDirection?: 'next' | 'previous';
    contentKey?: string | number;
    currentIndex?: number;
    totalQuestions?: number;
    className?: string;
    reviewee?: User;
    showScoresToTheReviewee?: boolean;
    showCommentsToTheReviewee?: boolean;
    feedbackPrivacy?: FeedbackPrivacyValue;
    /** Who the respondent of a 360 is to its results, so its reminder promises only what holds for them. */
    respondentStanding?: RespondentStanding;
    /** Completed responses an anonymous 360 needs before its answers become visible. */
    minimumResponses?: number;
    includeOther?: boolean;
    isOtherSelected?: boolean;
    otherText?: string;
    onOtherTextChange?: (text: string) => void;
    disabled?: boolean;
    dataTestId?: string;
    isAnswerValid?: boolean;
    isSaving?: boolean;
};

const DEFAULT_DURATION = 280;
const PROGRESS_BAR_GRADIENT = 'bg-gradient-purple';
const PROGRESS_COMPLETION_ANIMATION_MS = 800;
const MAX_OTHER_TEXT_LENGTH = 50;

export const SurveyAnswerContainer = ({
    question,
    renderAnswer,
    isLast,
    onNext,
    onPrevious,
    onSkip,
    onSubmit,
    backgroundImageUrl,
    showComment,
    commentValue,
    onCommentChange,
    commentLabel,
    enableTransitions = true,
    transitionDurationMs = DEFAULT_DURATION,
    transitionDirection = 'next',
    contentKey,
    currentIndex = 0,
    totalQuestions = 1,
    className = '',
    reviewee,
    showScoresToTheReviewee,
    showCommentsToTheReviewee,
    feedbackPrivacy,
    respondentStanding,
    minimumResponses = ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES,
    isPreview = false,
    includeOther = false,
    isOtherSelected = false,
    otherText = '',
    onOtherTextChange,
    disabled = false,
    dataTestId,
    isAnswerValid = true,
    isSaving = false,
}: SurveyAnswerContainerProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const managerLabel = getManagerLabel();

    const [isFinishing, setIsFinishing] = useState(false);

    const showCharLimitWarning = (otherText?.length ?? 0) >= MAX_OTHER_TEXT_LENGTH;

    const answerContent = useMemo(
        () => (typeof renderAnswer === 'function' ? renderAnswer() : renderAnswer),
        [renderAnswer],
    );

    const { launchedByReviewee, readsResults } = respondentStanding ?? {};

    const confidentialityMessage = useMemo(() => {
        if (feedbackPrivacy === FeedbackPrivacy.NAMED && launchedByReviewee) {
            return t('answer.confidentiality.namedLaunchedByReviewee', {
                managerLabel,
                revieweeSees: t(revieweeLaunchedCopy(FeedbackPrivacy.NAMED)),
            });
        }

        if (showScoresToTheReviewee === undefined && showCommentsToTheReviewee === undefined) {
            return t('answer.confidentiality.noResults', { managerLabel });
        }

        if (showScoresToTheReviewee === false && showCommentsToTheReviewee === false) {
            return t('answer.confidentiality.noResults', { managerLabel });
        }

        if (showScoresToTheReviewee && !showCommentsToTheReviewee) {
            return t('answer.confidentiality.scoresOnly', { managerLabel });
        }

        return t('answer.confidentiality.scoresAndComments', { managerLabel });
    }, [
        launchedByReviewee,
        feedbackPrivacy,
        showScoresToTheReviewee,
        showCommentsToTheReviewee,
        managerLabel,
        t,
    ]);

    // What the person reviewed sees does not depend on the mode: a 360 respondent is also told
    // whether their name goes with the answer, in a sentence where each language places the rest.
    const confidentiality = useMemo((): ConfidentialityNoticeProps => {
        if (isAnonymousFeedback(feedbackPrivacy)) {
            return {
                message: t('answer.confidentiality.anonymous'),
                iconName: FEEDBACK_PRIVACY_ICONS[FeedbackPrivacy.ANONYMOUS],
                details: (
                    <AnonymousPrivacyDetails
                        minimumResponses={minimumResponses}
                        respondentStanding={{ launchedByReviewee, readsResults }}
                    />
                ),
            };
        }

        if (feedbackPrivacy === FeedbackPrivacy.NAMED) {
            return {
                message: t('answer.confidentiality.named', { visibility: confidentialityMessage }),
                iconName: FEEDBACK_PRIVACY_ICONS[FeedbackPrivacy.NAMED],
            };
        }

        return { message: confidentialityMessage, iconName: 'security-eye-slash' };
    }, [
        feedbackPrivacy,
        launchedByReviewee,
        readsResults,
        minimumResponses,
        confidentialityMessage,
        t,
    ]);

    const handleOtherTextChange = useCallback(
        (value: string) => {
            const trimmedValue = value.trimStart();
            onOtherTextChange?.(trimmedValue);
        },
        [onOtherTextChange],
    );

    const handleNext = useCallback(() => {
        // isFinishing blocks re-entry until the deferred onSubmit has executed.
        if (isFinishing) {
            return;
        }
        if (isLast && onSubmit) {
            if (isAnswerValid) {
                setIsFinishing(true);
                setTimeout(onSubmit, PROGRESS_COMPLETION_ANIMATION_MS);
            } else {
                onSubmit();
            }
            return;
        }
        onNext();
    }, [isLast, onSubmit, onNext, isAnswerValid, isFinishing]);

    const progressPercentage = Math.round((currentIndex / totalQuestions) * 100);
    const displayedProgress = isFinishing ? 100 : progressPercentage;

    return (
        <SurveyWindow
            className={className}
            backgroundImageUrl={backgroundImageUrl}
            cardBottom={
                <ProgressBar
                    progressPercentage={displayedProgress}
                    gradientClassName={PROGRESS_BAR_GRADIENT}
                />
            }
            footer={
                <NavigationFooter
                    isLast={isLast}
                    isPreview={isPreview}
                    onNext={handleNext}
                    onPrevious={onPrevious}
                    onSkip={onSkip}
                    isSaving={isSaving || isFinishing}
                />
            }
        >
            {question.questionType !== QuestionType.SECTION_TITLE ? (
                <SurveyWindowContent>
                    <AnimatedAnswerContainer
                        question={question}
                        transitionDurationMs={transitionDurationMs}
                        transitionDirection={transitionDirection}
                        contentKey={contentKey}
                        enableTransitions={enableTransitions}
                    >
                        <QuestionContent
                            question={question}
                            answerContent={answerContent}
                            confidentiality={confidentiality}
                            reviewee={reviewee}
                            showComment={showComment}
                            commentValue={commentValue}
                            commentLabel={commentLabel}
                            onCommentChange={onCommentChange}
                            onNext={handleNext}
                            includeOther={includeOther}
                            isOtherSelected={isOtherSelected}
                            otherText={otherText}
                            onOtherTextChange={handleOtherTextChange}
                            showCharLimitWarning={showCharLimitWarning}
                            maxOtherTextLength={MAX_OTHER_TEXT_LENGTH}
                            disabled={disabled}
                            dataTestId={dataTestId}
                        />
                    </AnimatedAnswerContainer>
                </SurveyWindowContent>
            ) : (
                <SurveyWindowSection>
                    <AnimatedAnswerContainer
                        question={question}
                        transitionDurationMs={transitionDurationMs}
                        transitionDirection={transitionDirection}
                        contentKey={contentKey}
                        enableTransitions={enableTransitions}
                    >
                        {answerContent}
                    </AnimatedAnswerContainer>
                </SurveyWindowSection>
            )}
        </SurveyWindow>
    );
};

export default SurveyAnswerContainer;
